import { useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, m } from "framer-motion";
import { ChevronDown, Download, FileText, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

// Legacy source: src/components/billing/invoices-section.tsx (1:1 port).
// Fiscal invoices list (subscription + self-billing) with per-row PDF download.

interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  type: "subscription" | "self_billing";
  status: string;
  recipientName: string;
  recipientFiscalType: string;
  subtotal: number;
  vatRate: number;
  vatTotal: number;
  total: number;
  currency: string;
  vatNote: string | null;
  issuedAt: string;
  paidAt: string | null;
}

interface InvoicesResponse {
  invoices: InvoiceRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(amount: number, currency: string) {
  const symbol = currency === "EUR" ? "€" : currency;
  return `${symbol}${amount.toFixed(2)}`;
}

export function InvoicesSection() {
  const { t } = useLingui();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ["api", "/api/account/billing/invoices", { limit: 50 }],
    queryFn: () => apiFetch.get<InvoicesResponse>("/api/account/billing/invoices?limit=50"),
  });

  const invoices = query.data?.invoices ?? [];

  const handleDownload = async (invoice: InvoiceRow) => {
    setDownloadingId(invoice.id);
    try {
      const res = await apiFetch.raw(
        `/api/account/billing/invoices/${encodeURIComponent(invoice.id)}/pdf`,
        { method: "GET" },
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${invoice.invoiceNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  if (query.isLoading) {
    return (
      <div className="bg-card rounded-xl border p-6">
        <div className="bg-muted mb-4 h-6 w-40 animate-pulse rounded" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-muted h-14 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border p-6">
      <div className="flex items-center gap-2">
        <FileText className="text-primary h-5 w-5" />
        <h2 className="text-foreground text-lg font-semibold">
          <Trans>Invoices</Trans>
        </h2>
      </div>
      <p className="text-muted-foreground mt-1 text-sm">
        <Trans>Fiscal invoices issued by Appy Solutions</Trans>
      </p>

      {invoices.length === 0 ? (
        <div className="mt-6 flex flex-col items-center rounded-lg border-2 border-dashed py-10">
          <FileText className="text-muted-foreground/40 h-10 w-10" />
          <p className="text-muted-foreground mt-3 text-sm font-medium">
            <Trans>No invoices issued yet</Trans>
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {/* Desktop header */}
          <div className="text-muted-foreground hidden grid-cols-6 gap-2 px-4 pb-2 text-xs font-medium md:grid">
            <span>
              <Trans>Date</Trans>
            </span>
            <span>
              <Trans>Invoice #</Trans>
            </span>
            <span>
              <Trans>Type</Trans>
            </span>
            <span className="text-right">
              <Trans>Subtotal</Trans>
            </span>
            <span className="text-right">
              <Trans>VAT</Trans>
            </span>
            <span className="text-right">
              <Trans>Total</Trans>
            </span>
          </div>

          {invoices.map((inv, index) => (
            <m.div
              key={inv.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
            >
              {/* Desktop row */}
              <div className="bg-background hidden grid-cols-6 items-center gap-2 rounded-lg border p-4 md:grid">
                <span className="text-foreground text-sm">{formatDate(inv.issuedAt)}</span>
                <span className="text-foreground text-sm font-medium">{inv.invoiceNumber}</span>
                <span>
                  <Badge variant="outline" className="text-xs">
                    {inv.type === "subscription" ? (
                      <Trans>Subscription</Trans>
                    ) : (
                      <Trans>Self-billing</Trans>
                    )}
                  </Badge>
                </span>
                <span className="text-foreground text-right text-sm">
                  {formatCurrency(inv.subtotal, inv.currency)}
                </span>
                <span className="text-muted-foreground text-right text-sm">
                  {inv.vatTotal > 0 ? formatCurrency(inv.vatTotal, inv.currency) : "—"}
                </span>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-foreground text-sm font-semibold">
                    {formatCurrency(inv.total, inv.currency)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    aria-label={t`Download PDF`}
                    onClick={() => handleDownload(inv)}
                    disabled={downloadingId === inv.id}
                  >
                    {downloadingId === inv.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Mobile card */}
              <div className="bg-background rounded-lg border md:hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === inv.id ? null : inv.id)}
                  className="flex min-h-[44px] w-full items-center justify-between p-4 text-left"
                >
                  <div>
                    <p className="text-foreground text-sm font-medium">{inv.invoiceNumber}</p>
                    <p className="text-muted-foreground text-xs">{formatDate(inv.issuedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground text-sm font-semibold">
                      {formatCurrency(inv.total, inv.currency)}
                    </span>
                    <ChevronDown
                      className={`text-muted-foreground h-4 w-4 transition-transform ${expandedId === inv.id ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>
                <AnimatePresence>
                  {expandedId === inv.id && (
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t px-4 pt-3 pb-4">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">
                              <Trans>Type</Trans>
                            </p>
                            <Badge variant="outline" className="mt-0.5 text-xs">
                              {inv.type === "subscription" ? (
                                <Trans>Subscription</Trans>
                              ) : (
                                <Trans>Self-billing</Trans>
                              )}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">
                              <Trans>Status</Trans>
                            </p>
                            <span className="text-foreground text-sm capitalize">{inv.status}</span>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">
                              <Trans>Subtotal</Trans>
                            </p>
                            <span className="text-foreground">
                              {formatCurrency(inv.subtotal, inv.currency)}
                            </span>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">
                              <Trans>VAT</Trans>
                            </p>
                            <span className="text-foreground">
                              {inv.vatTotal > 0
                                ? `${formatCurrency(inv.vatTotal, inv.currency)} (${inv.vatRate}%)`
                                : "—"}
                            </span>
                          </div>
                        </div>
                        {inv.vatNote && (
                          <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                            {inv.vatNote}
                          </p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 w-full"
                          onClick={() => handleDownload(inv)}
                          disabled={downloadingId === inv.id}
                        >
                          {downloadingId === inv.id ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="mr-1.5 h-3.5 w-3.5" />
                          )}
                          <Trans>Download PDF</Trans>
                        </Button>
                      </div>
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            </m.div>
          ))}
        </div>
      )}
    </div>
  );
}
